import type { ReactNode } from "react";
import { Spreekballon } from "../Spreekballon";
import { useT } from "../../lib/use-t";

interface SchrijvenRunnerShellProps {
  /** Task-type label, e.g. "E-mail" — shown in the top-bar pill. */
  taskLabel: string;
  /** Step counter for exam mode; null hides it (practice). */
  stepCounter?: { n: number; total: number } | null;
  /** Optional task switcher (exam chip strip) rendered next to the pill. */
  taskSwitcher?: ReactNode;
  /** Optional timer node. Omitted in v1 (writing is untimed) — pass null. */
  timer?: ReactNode;
  /** Opens the ExitConfirmDialog. */
  onClose: () => void;
  /** Opdracht card (desktop left column / mobile `<details>` body). */
  opdracht: ReactNode;
  /** Mobile opdracht summary heading (task label). */
  opdrachtTitle: string;
  /** Composer shell (right column). */
  editor: ReactNode;
  /** Static writing-tips panel (or null). */
  tutor?: ReactNode;
  /** Word-count meter for the bottom bar (desktop). */
  wordMeter: ReactNode;
  /** Compact word-count meter for the mobile bottom bar. */
  wordMeterCompact: ReactNode;
  /** Bottom-bar actions (Concept bewaren + Lever in). */
  actions: ReactNode;
  /** Compact mobile action (Lever in only). */
  actionsCompact: ReactNode;
}

/**
 * Full-screen, fixed-height runner shell for the Schrijven writing session.
 * Owns its OWN slim top bar (chromeless — no app sidebar/tab bar). Desktop is a
 * `360px | 1fr` grid with internal scroll on the right column; mobile collapses
 * to a single scrolling column with the opdracht as a `<details>` disclosure
 * and a sticky bottom action bar.
 */
export function SchrijvenRunnerShell({
  taskLabel,
  stepCounter,
  taskSwitcher,
  timer,
  onClose,
  opdracht,
  opdrachtTitle,
  editor,
  tutor,
  wordMeter,
  wordMeterCompact,
  actions,
  actionsCompact,
}: SchrijvenRunnerShellProps) {
  const t = useT();

  const pill = (label: string) => (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--accent-soft)] px-2.5 py-1.5 font-mono-badge text-[10px] uppercase tracking-wider text-[var(--accent)]">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
        <path d="M5 16l9.5-9.5a2 2 0 012.8 0l.2.2a2 2 0 010 2.8L8 19l-4 1z" />
      </svg>
      {label}
    </span>
  );

  return (
    <div className="h-dvh flex flex-col bg-[var(--bg)]">
      {/* ── Desktop top bar ───────────────────────────────────────────── */}
      <header className="hidden lg:flex items-center gap-4 px-6 py-3.5 border-b border-[var(--border)] bg-[var(--panel)]">
        <Spreekballon size={26} />
        <span className="font-display font-semibold text-[17px] tracking-tight">
          <span className="text-[var(--accent)]">A2</span> Easy
        </span>
        {pill(t("schrijven.runner.taskPillFmt", { task: taskLabel }))}
        {taskSwitcher}
        {stepCounter ? (
          <span className="mx-auto font-mono-badge text-sm text-[var(--ink-faint)]">
            {t("schrijven.runner.stepFmt", { n: stepCounter.n, total: stepCounter.total })}
          </span>
        ) : (
          <span className="mx-auto" />
        )}
        {timer}
        <CloseButton onClose={onClose} label={t("schrijven.runner.close")} />
      </header>

      {/* ── Mobile top bar ────────────────────────────────────────────── */}
      <header className="flex lg:hidden items-center gap-2.5 px-4 py-3 border-b border-[var(--border)] bg-[var(--panel)]">
        {pill(taskLabel)}
        <span className="ml-auto" />
        {timer}
        <CloseButton onClose={onClose} label={t("schrijven.runner.close")} iconOnly />
      </header>

      {/* ── Desktop body ──────────────────────────────────────────────── */}
      <div className="hidden lg:grid flex-1 min-h-0 grid-cols-[360px_1fr] gap-[22px] p-6 overflow-hidden">
        <div className="min-h-0 overflow-auto">{opdracht}</div>
        <div className="flex flex-col gap-[18px] min-h-0 overflow-auto">
          {editor}
          {tutor}
        </div>
      </div>

      {/* ── Mobile body ───────────────────────────────────────────────── */}
      <div className="flex lg:hidden flex-1 min-h-0 flex-col gap-3.5 p-4 overflow-auto">
        <details
          open
          className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] shadow-[var(--shadow)] overflow-hidden [&_summary::-webkit-details-marker]:hidden group"
        >
          <summary className="list-none cursor-pointer px-[18px] py-[15px] flex items-center gap-2.5">
            <span>
              <span className="block font-mono-badge text-[11px] uppercase tracking-wider text-[var(--ink-faint)]">
                {t("schrijven.runner.opdrachtEyebrow")}
              </span>
              <span className="font-display font-semibold text-[16px] text-[var(--ink)]">
                {opdrachtTitle}
              </span>
            </span>
            <span className="ml-auto text-[var(--ink-faint)] transition-transform duration-200 group-open:rotate-90" aria-hidden>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </span>
          </summary>
          <div className="px-[18px] pb-[18px]">{opdracht}</div>
        </details>

        {editor}
        {tutor}
      </div>

      {/* ── Desktop bottom bar ────────────────────────────────────────── */}
      <footer className="hidden lg:flex items-center gap-4 px-6 py-3.5 border-t border-[var(--border)] bg-[var(--panel)]">
        {wordMeter}
        <div className="ml-auto flex items-center gap-3">{actions}</div>
      </footer>

      {/* ── Mobile bottom bar ─────────────────────────────────────────── */}
      <footer className="flex lg:hidden items-center gap-3 px-4 py-3 border-t border-[var(--border)] bg-[var(--panel)]">
        {wordMeterCompact}
        <div className="ml-auto">{actionsCompact}</div>
      </footer>
    </div>
  );
}

function CloseButton({
  onClose,
  label,
  iconOnly = false,
}: {
  onClose: () => void;
  label: string;
  iconOnly?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={label}
      className="inline-flex items-center justify-center gap-1.5 min-h-11 min-w-11 rounded-full border border-[var(--border-strong)] bg-transparent px-3.5 py-2 text-[13px] font-semibold text-[var(--ink-muted)] transition-colors duration-150 ease-out hover:bg-[var(--panel-soft)] hover:text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2"
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
      {iconOnly ? null : label}
    </button>
  );
}
